import { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, BookOpen, CheckCircle2, Lightbulb, ListChecks, Sigma } from 'lucide-react';
import QuestionVisual from '../quiz/QuestionVisual';

/**
 * Detecte le type de media a partir d'une URL.
 * Inspire du mediaUtils.js de Nizus.
 */
function detectMediaType(url) {
    if (!url) return null;
    const lower = url.toLowerCase();
    if (/\.(jpg|jpeg|png|gif|webp|svg)(\?|$)/.test(lower)) return 'image';
    if (/\.(mp4|webm|ogg)(\?|$)/.test(lower)) return 'video-file';
    if (/youtube\.com|youtu\.be|youtube-nocookie\.com/.test(lower)) return 'youtube';
    if (/vimeo\.com/.test(lower)) return 'vimeo';
    return 'image'; // fallback
}

/**
 * Extrait l'URL d'embed YouTube a partir d'une URL standard.
 */
function getYouTubeEmbedUrl(url) {
    if (!url) return '';
    let videoId = '';
    const shortMatch = url.match(/youtu\.be\/([a-zA-Z0-9_-]{11})/);
    const longMatch = url.match(/[?&]v=([a-zA-Z0-9_-]{11})/);
    const embedMatch = url.match(/embed\/([a-zA-Z0-9_-]{11})/);
    videoId = shortMatch?.[1] || longMatch?.[1] || embedMatch?.[1] || '';
    if (!videoId) return '';
    return `https://www.youtube-nocookie.com/embed/${videoId}`;
}

/**
 * Extrait l'URL d'embed Vimeo.
 */
function getVimeoEmbedUrl(url) {
    if (!url) return '';
    const match = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    if (!match) return url.includes('player.vimeo.com') ? url : '';
    return `https://player.vimeo.com/video/${match[1]}`;
}

/**
 * Lightbox plein écran (refaite le 27/10/2026 pour les cartes) : l'image remplit l'écran à l'ouverture,
 * la molette et le pincement zooment vers le point visé, le glissé déplace l'image sans retard,
 * le double-clic alterne 1x / 2,5x. Un glissé ne ferme jamais la fenêtre ; Échap la ferme.
 */
const ZOOM_MIN = 1;
const ZOOM_MAX = 6;

function Lightbox({ src, alt, onClose }) {
    const overlayRef = useRef(null);
    const imgRef = useRef(null);
    const [view, setView] = useState({ s: 1, x: 0, y: 0 });
    const viewRef = useRef(view);
    viewRef.current = view;
    const pointers = useRef(new Map());
    const geste = useRef(null);   // { type: 'pan' | 'pinch', ... }
    const aGlisse = useRef(false);
    const downSurFond = useRef(false); // avec la capture du pointeur, le clic arrive sur le fond : on retient la vraie cible
    const [enGeste, setEnGeste] = useState(false);

    // Borne le déplacement : l'image ne peut pas sortir de l'écran.
    const borne = useCallback((v) => {
        const img = imgRef.current;
        if (!img) return v;
        const s = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, v.s));
        // Taille réellement dessinée (object-fit: contain laisse des bandes vides dans la boîte de l'image).
        const r = img.naturalWidth && img.naturalHeight ? img.naturalWidth / img.naturalHeight : img.offsetWidth / img.offsetHeight;
        const w = Math.min(img.offsetWidth, img.offsetHeight * r);
        const h = Math.min(img.offsetHeight, img.offsetWidth / r);
        const mx = Math.max(0, (w * s - window.innerWidth) / 2 + 40);
        const my = Math.max(0, (h * s - window.innerHeight) / 2 + 40);
        return { s, x: Math.min(mx, Math.max(-mx, v.x)), y: Math.min(my, Math.max(-my, v.y)) };
    }, []);

    // Zoom de facteur f centré sur le point (cx, cy) de l'écran : ce point reste sous le curseur.
    const zoomVers = useCallback((f, cx, cy) => {
        const v = viewRef.current;
        const ns = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, v.s * f));
        const dx = cx - window.innerWidth / 2;
        const dy = cy - window.innerHeight / 2;
        const k = ns / v.s;
        setView(borne({ s: ns, x: dx - (dx - v.x) * k, y: dy - (dy - v.y) * k }));
    }, [borne]);

    useEffect(() => {
        const el = overlayRef.current;
        // Écouteur natif non passif : sinon la molette fait défiler la page derrière.
        const onWheel = (e) => {
            e.preventDefault();
            zoomVers(Math.exp(-e.deltaY * 0.0015), e.clientX, e.clientY);
        };
        const onKey = (e) => {
            if (e.key === 'Escape') onClose();
            else if (e.key === '+' || e.key === '=') zoomVers(1.4, window.innerWidth / 2, window.innerHeight / 2);
            else if (e.key === '-') zoomVers(1 / 1.4, window.innerWidth / 2, window.innerHeight / 2);
        };
        el.addEventListener('wheel', onWheel, { passive: false });
        window.addEventListener('keydown', onKey);
        const overflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            el.removeEventListener('wheel', onWheel);
            window.removeEventListener('keydown', onKey);
            document.body.style.overflow = overflow;
        };
    }, [zoomVers, onClose]);

    const handlePointerDown = (e) => {
        if (e.target.closest('button')) return;
        if (pointers.current.size === 0) downSurFond.current = e.target === overlayRef.current;
        overlayRef.current.setPointerCapture(e.pointerId);
        setEnGeste(true);
        pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
        aGlisse.current = false;
        const pts = [...pointers.current.values()];
        const v = viewRef.current;
        if (pts.length === 2) {
            const [p, q] = pts;
            geste.current = { type: 'pinch', d0: Math.hypot(p.x - q.x, p.y - q.y), v0: v, cx: (p.x + q.x) / 2, cy: (p.y + q.y) / 2 };
        } else if (pts.length === 1) {
            geste.current = { type: 'pan', x0: e.clientX, y0: e.clientY, v0: v };
        }
    };
    const handlePointerMove = (e) => {
        if (!pointers.current.has(e.pointerId)) return;
        pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
        const g = geste.current;
        if (!g) return;
        if (g.type === 'pan') {
            const dx = e.clientX - g.x0, dy = e.clientY - g.y0;
            if (Math.abs(dx) + Math.abs(dy) > 4) aGlisse.current = true;
            if (g.v0.s > 1) setView(borne({ s: g.v0.s, x: g.v0.x + dx, y: g.v0.y + dy }));
        } else if (g.type === 'pinch' && pointers.current.size === 2) {
            const [p, q] = [...pointers.current.values()];
            aGlisse.current = true;
            const ns = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, g.v0.s * Math.hypot(p.x - q.x, p.y - q.y) / g.d0));
            const dx = g.cx - window.innerWidth / 2, dy = g.cy - window.innerHeight / 2;
            const k = ns / g.v0.s;
            setView(borne({ s: ns, x: dx - (dx - g.v0.x) * k, y: dy - (dy - g.v0.y) * k }));
        }
    };
    const handlePointerUp = (e) => {
        pointers.current.delete(e.pointerId);
        const reste = [...pointers.current.values()];
        // Après un pincement, le doigt restant reprend le déplacement depuis sa position.
        geste.current = reste.length === 1 ? { type: 'pan', x0: reste[0].x, y0: reste[0].y, v0: viewRef.current } : null;
        if (reste.length === 0) setEnGeste(false);
    };

    const handleClick = (e) => {
        if (aGlisse.current) { aGlisse.current = false; return; }
        // Clic sur le fond (hors image) : fermeture.
        if (downSurFond.current && !e.target.closest('button')) onClose();
    };
    const handleDoubleClick = (e) => {
        if (e.target.closest('button')) return;
        if (viewRef.current.s > 1.05) setView({ s: 1, x: 0, y: 0 });
        else zoomVers(2.5, e.clientX, e.clientY);
    };

    const centre = () => ({ cx: window.innerWidth / 2, cy: window.innerHeight / 2 });
    const zoomIn = () => { const { cx, cy } = centre(); zoomVers(1.5, cx, cy); };
    const zoomOut = () => { const { cx, cy } = centre(); zoomVers(1 / 1.5, cx, cy); };
    const resetZoom = () => setView({ s: 1, x: 0, y: 0 });

    // Portail vers <body> : dans la question, un parent transformé capturait le position: fixed (fenêtre coincée dans la carte).
    return createPortal(
        <div
            ref={overlayRef}
            className="rc-lightbox-overlay"
            onClick={handleClick}
            onDoubleClick={handleDoubleClick}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            style={{ cursor: view.s > 1 ? (enGeste ? 'grabbing' : 'grab') : 'zoom-in' }}
        >
            <div className="rc-lightbox-controls">
                <button onClick={zoomOut} title="Dézoomer (−)" disabled={view.s <= ZOOM_MIN}>−</button>
                <button onClick={resetZoom} title="Taille écran">{Math.round(view.s * 100)}%</button>
                <button onClick={zoomIn} title="Zoomer (+)" disabled={view.s >= ZOOM_MAX}>+</button>
            </div>
            <button className="rc-lightbox-close" onClick={onClose} aria-label="Fermer">✕</button>
            <p className="rc-lightbox-aide">Molette ou pincement pour zoomer · glisser pour se déplacer · double-clic pour agrandir</p>
            <img
                ref={imgRef}
                src={src}
                alt={alt || ''}
                className={`rc-lightbox-img${enGeste ? '' : ' rc-lightbox-img--anim'}`}
                style={{ transform: `translate(${view.x}px, ${view.y}px) scale(${view.s})` }}
                draggable={false}
            />
        </div>,
        document.body
    );
}

/**
 * Bloc media (image avec lightbox).
 */
function ImageBlock({ block }) {
    const [lightboxOpen, setLightboxOpen] = useState(false);

    if (!block.src) return null;

    // Normalize src: ensure local paths start with /
    const src = block.src.startsWith('uploads/') ? `/${block.src}` : block.src;

    return (
        <>
            <figure
                className={`rc-media rc-media--${block.position || 'center'}`}
                style={{ width: block.width ? `${block.width}%` : '100%' }}
            >
                <div className="rc-media__img-wrap" onClick={() => setLightboxOpen(true)}>
                    <img
                        src={src}
                        alt={block.alt || ''}
                        className="rc-media__img rc-media__img--clickable"
                        loading="lazy"
                    />
                    <span className="rc-media__zoom-hint">🔍</span>
                </div>
                {block.caption && <figcaption className="rc-media__caption">{block.caption}</figcaption>}
            </figure>
            {lightboxOpen && (
                <Lightbox src={src} alt={block.alt} onClose={() => setLightboxOpen(false)} />
            )}
        </>
    );
}

/**
 * Bloc video (YouTube, Vimeo, ou fichier video).
 */
function VideoBlock({ block }) {
    if (!block.src) return null;

    const type = detectMediaType(block.src);
    let embedUrl = block.src;

    if (type === 'youtube') {
        embedUrl = getYouTubeEmbedUrl(block.src);
        if (!embedUrl) return null;
    } else if (type === 'vimeo') {
        embedUrl = getVimeoEmbedUrl(block.src);
        if (!embedUrl) return null;
    }

    return (
        <figure
            className={`rc-media rc-media--${block.position || 'center'}`}
            style={{ width: block.width ? `${block.width}%` : '100%' }}
        >
            {type === 'video-file' ? (
                <video
                    controls
                    className="rc-media__video"
                    preload="metadata"
                >
                    <source src={block.src} />
                </video>
            ) : (
                <div className="rc-media__embed-wrapper">
                    <iframe
                        src={embedUrl}
                        className="rc-media__embed"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        loading="lazy"
                        title={block.caption || 'Video'}
                    />
                </div>
            )}
            {block.caption && <figcaption className="rc-media__caption">{block.caption}</figcaption>}
        </figure>
    );
}

/**
 * Blocs de fiche (notions, migration 187) : intertitre, liste, tableau, encadré. Le HTML des cellules et éléments
 * est celui des paragraphes (gras, italique, liens), nettoyé côté serveur (Sanitizer).
 */
function HeadingBlock({ block }) {
    const Tag = block.level === 4 ? 'h4' : 'h3';
    return <Tag className="rc__heading" dangerouslySetInnerHTML={{ __html: block.content || '' }} />;
}
function ListBlock({ block }) {
    const Tag = block.ordered ? 'ol' : 'ul';
    return (
        <Tag className="rc__list">
            {(block.items || []).map((it, i) => <li key={i} dangerouslySetInnerHTML={{ __html: it }} />)}
        </Tag>
    );
}
function TableBlock({ block }) {
    return (
        <div className="rc__table-wrap">
            <table className="rc__table">
                {block.headers?.length > 0 && (
                    <thead><tr>{block.headers.map((h, i) => <th key={i} dangerouslySetInnerHTML={{ __html: h }} />)}</tr></thead>
                )}
                <tbody>
                    {(block.rows || []).map((r, i) => (
                        <tr key={i}>{r.map((c, j) => <td key={j} dangerouslySetInnerHTML={{ __html: c }} />)}</tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
// Encadrés de fiche : le ton porte une étiquette (Définition, Formule…) ; « info » reste un encadré neutre.
export const CALLOUT_TONES = {
    info: { label: '', Icon: null },
    definition: { label: 'Définition', Icon: BookOpen },
    formule: { label: 'Formule', Icon: Sigma },
    methode: { label: 'Méthode', Icon: ListChecks },
    exemple: { label: 'Exemple', Icon: Lightbulb },
    warning: { label: 'Piège', Icon: AlertTriangle },
    retenir: { label: 'À retenir', Icon: CheckCircle2 },
};

function CalloutBlock({ block }) {
    const tone = CALLOUT_TONES[block.tone] ? block.tone : 'info';
    const { label, Icon } = CALLOUT_TONES[tone];
    return (
        <aside className={`rc__callout rc__callout--${tone}`}>
            {label && <span className="rc__callout-tag">{Icon && <Icon size={14} />} {label}</span>}
            {block.title && <p className="rc__callout-title" dangerouslySetInnerHTML={{ __html: block.title }} />}
            <RichContent blocks={block.blocks || []} />
        </aside>
    );
}

/**
 * Composant principal de rendu de contenu riche.
 * Supporte les blocs : paragraph, image, video, renderer, et pour les fiches heading, list, table, callout.
 *
 * @param {Array} blocks - Tableau de blocs de contenu JSON
 * @param {string} className - Classe CSS additionnelle
 */
export default function RichContent({ blocks, className = '' }) {
    if (!blocks || !Array.isArray(blocks)) return null;

    const activeBlocks = blocks.filter(b => b.isActive !== false);
    if (activeBlocks.length === 0) return null;

    return (
        <div className={`rc ${className}`.trim()}>
            {activeBlocks.map((block, index) => {
                switch (block.type) {
                    case 'paragraph':
                        return (
                            <p
                                key={index}
                                className="rc__paragraph"
                                dangerouslySetInnerHTML={{ __html: block.content }}
                            />
                        );
                    case 'image':
                        return <ImageBlock key={index} block={block} />;
                    case 'video':
                        return <VideoBlock key={index} block={block} />;
                    case 'renderer':
                        return <QuestionVisual key={index} renderer={block.renderer} data={block.data} />;
                    case 'heading':
                        return <HeadingBlock key={index} block={block} />;
                    case 'list':
                        return <ListBlock key={index} block={block} />;
                    case 'table':
                        return <TableBlock key={index} block={block} />;
                    case 'callout':
                        return <CalloutBlock key={index} block={block} />;
                    default:
                        return null;
                }
            })}
        </div>
    );
}
